// skeleton.tsx — the shimmer block that replaces the string "Loading…" everywhere in the
// dashboard. Sized by the caller to match the real element it stands in for, so nothing
// shifts when data lands (handoff README §6). The sweep is a named class, not an inline
// Tailwind arbitrary-value animation, so it can be killed under prefers-reduced-motion
// (globals.css) the same way StatusChip's working-state dot is — the handoff's Shell
// Anatomy §06 explicitly puts this animation "inside the reduced-motion kill switch".
import * as React from 'react';

import { cn } from '../lib/utils.ts';

function Skeleton({
  className,
  /** Subtitle-skeleton spec §C (2026-08-22) — defaults to `'div'` (every existing caller), but
   * an inline placeholder that has to sit INSIDE a `<p>` (e.g. `PageHeader`'s `subtitleLoading`)
   * needs `as="span"`: a `<div>` is not valid content inside a `<p>` — browsers silently close
   * the `<p>` early when they hit one, which would break the exact "same element" structure
   * that use case exists to guarantee. */
  as: Component = 'div',
  ...props
}: React.ComponentProps<'div'> & { as?: 'div' | 'span' }) {
  return (
    <Component
      aria-hidden="true"
      data-slot="skeleton"
      className={cn(
        'skeleton-sweep rounded-md',
        'bg-[linear-gradient(90deg,var(--muted)_25%,var(--accent)_37%,var(--muted)_63%)]',
        'bg-[length:300%_100%]',
        className,
      )}
      {...props}
    />
  );
}

/** A single line of placeholder text at the real line-height, so a paragraph of skeletons
 * occupies exactly the height the copy will. Radius 6 for text bars vs. 8 for blocks
 * (handoff README §6) — deliberately narrower than the base `Skeleton`. */
function SkeletonText({ className, ...props }: React.ComponentProps<'div'>) {
  return <Skeleton className={cn('h-[13px] rounded-sm', className)} {...props} />;
}

export { Skeleton, SkeletonText };
